import { useCallback, useEffect, useState } from "react";

export type Value = string | null | undefined;

// The browser `storage` event only fires in *other* tabs, so multiple
// useLocalStorage instances in the same tab don't see each other's writes.
// This in-memory registry broadcasts a write to every same-tab subscriber so
// changes apply live without a reload.
const sameTabSubscribers = new Map<string, Set<(value: Value) => void>>();

function subscribeSameTab(key: string, callback: (value: Value) => void) {
  const subscribers = sameTabSubscribers.get(key) ?? new Set();
  subscribers.add(callback);
  sameTabSubscribers.set(key, subscribers);

  return () => {
    subscribers.delete(callback);
    if (subscribers.size === 0) {
      sameTabSubscribers.delete(key);
    }
  };
}

function emitSameTab(key: string, value: Value) {
  sameTabSubscribers.get(key)?.forEach((callback) => callback(value));
}

export function useLocalStorage(
  key: string | undefined
): [value: Value, set: (value: Value) => Value] {
  const [value, setValue] = useState(() => {
    return key == null ? undefined : localStorage.getItem(key);
  });

  useEffect(() => {
    if (key == null) {
      setValue(undefined);
      return;
    }

    setValue(localStorage.getItem(key));

    const onStorage = (e: StorageEvent) => {
      if (e.key !== key) {
        return;
      }
      setValue(e.newValue);
    };

    window.addEventListener("storage", onStorage);
    const unsubscribeSameTab = subscribeSameTab(key, setValue);

    return () => {
      window.removeEventListener("storage", onStorage);
      unsubscribeSameTab();
    };
  }, [key]);

  const set = useCallback(
    (value: string | null | undefined) => {
      if (key == null) {
        throw new Error("Could not set value, because storage key is null");
      }

      if (value == null) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, value);
      }

      setValue(value);
      emitSameTab(key, value);

      return value;
    },
    [key]
  );

  return [value, set];
}
